import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { isLabelInUse } from 'utils/accounts';
import messages from './messages';

export interface NameAccountDialogProps {
  open: boolean;
  initialValue?: string;
  emailHint?: string;
  title?: string;
  /** Labels already used by other accounts — a case-insensitive match blocks saving. */
  takenLabels?: string[];
  onSubmit: (label: string) => void;
  onCancel?: () => void;
}

export const NameAccountDialog: FC<NameAccountDialogProps> = ({
  open,
  initialValue = '',
  emailHint,
  title,
  takenLabels = [],
  onSubmit,
  onCancel,
}) => {
  const { formatMessage } = useIntl();
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    if (open) setValue(initialValue);
  }, [open, initialValue]);

  const trimmed = value.trim();
  const isDuplicate = isLabelInUse(trimmed, takenLabels);
  const canSubmit = !!trimmed && !isDuplicate;

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>{title ?? formatMessage(messages.nameDialogTitle)}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          margin="dense"
          label={formatMessage(messages.nameLabel)}
          error={isDuplicate}
          helperText={isDuplicate ? formatMessage(messages.duplicateName) : emailHint}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && canSubmit) onSubmit(trimmed);
          }}
        />
      </DialogContent>
      <DialogActions>
        {onCancel && (
          <Button onClick={onCancel} variant="contained" color="secondary">
            {formatMessage(messages.cancel)}
          </Button>
        )}
        <Button variant="contained" disabled={!canSubmit} onClick={() => onSubmit(trimmed)}>
          {formatMessage(messages.save)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
